import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import configs from "../../utils/configs";
import { applyRuntimeTheme, createRuntimeTheme, getCurrentTheme, registerDarkModeQuery } from "../../utils/theme";

function useDarkMode(observe = true) {
  const [darkMode, setDarkMode] = useState(false);

  const changeListener = useCallback(
    event => {
      setDarkMode(event.matches);
    },
    [setDarkMode]
  );

  useEffect(() => {
    if (!observe) return undefined;
    const [darkModeQuery, removeListener] = registerDarkModeQuery(changeListener);

    setDarkMode(darkModeQuery.matches);

    return removeListener;
  }, [changeListener, observe]);

  return darkMode;
}

export function useTheme(appearance) {
  const darkMode = useDarkMode(appearance === "system");

  useEffect(() => {
    applyRuntimeTheme(createRuntimeTheme(getCurrentTheme()));
  }, [darkMode]);
}

function getAppLogo(darkMode) {
  const theme = getCurrentTheme();
  const shouldUseDarkLogo = theme ? theme.appearance === "dark" : darkMode;
  return shouldUseDarkLogo ? configs.image("logo_dark") : configs.image("logo");
}

function getMiniAppLogo() {
  return configs.image("app_icon");
}

export function useLogo() {
  const darkMode = useDarkMode();
  return getAppLogo(darkMode);
}

export function useMinilogo() {
  return getMiniAppLogo();
}

export function useThemeFromStore(store) {
  const [appearance, setAppearance] = useState(store?.state?.preferences?.appearance);

  useEffect(() => {
    function onStoreChanged() {
      const nextAppearance = store.state?.preferences?.appearance;

      if (appearance !== nextAppearance) {
        setAppearance(nextAppearance);
      }
    }

    if (store) {
      store.addEventListener("statechanged", onStoreChanged);
    }

    return () => {
      if (store) {
        store.removeEventListener("statechanged", onStoreChanged);
      }
    };
  });

  useTheme(appearance);
}

export function ThemeProvider({ store, children }) {
  useThemeFromStore(store);
  return children;
}

ThemeProvider.propTypes = {
  store: PropTypes.object,
  children: PropTypes.node
};
